import {
  DEFAULT_PAYMENT_WINDOW_MINUTES,
  isPickupPaymentTiming,
  normalizePaymentWindowMinutes,
  normalizePickupApprovalMode,
  type PickupApprovalMode,
  type PickupPaymentTiming,
} from '@/lib/pickup-approval';

export type BranchOrderingSettings = {
  tableOrderingEnabled: boolean;
  counterPickupEnabled: boolean;
  counterPickupRequireEntryCode: boolean;
  counterPickupRequireGuestName: boolean;
  counterPickupEntryCode: string | null;
  dineInGeofenceRequired: boolean;
  /**
   * Whether the counter vets a pickup order before the guest is asked to pay
   * (docs/operational-ux-2026-09/07-customer-ordering.md). Defaults to
   * `instant`, which is exactly today's behaviour, so no existing branch changes
   * until someone turns it on.
   */
  pickupApprovalMode: PickupApprovalMode;
  /** How long an accepted order waits for payment before it expires. */
  pickupPaymentWindowMinutes: number;
  /**
   * When the money is taken for an order-ahead pickup order.
   *
   * `null` means the owner has NEVER CHOSEN, which is a real state and not a
   * synonym for `pay_now`: for those rows `resolvePickupPaymentTiming` infers
   * today's behaviour from the branch's rail. The distinction is preserved all
   * the way to the UI so the settings page can say "inherited" rather than
   * inventing a default the owner never set.
   *
   * Deliberately a separate question from `pickupApprovalMode` — see the header
   * of `lib/pickup-approval.ts`.
   */
  pickupPaymentTiming: PickupPaymentTiming | null;
};

type OrderingRecord = Record<string, unknown>;

function asOrderingRecord(input: unknown): OrderingRecord {
  return input && typeof input === 'object' && !Array.isArray(input)
    ? (input as OrderingRecord)
    : {};
}

function readBoolean(value: unknown, fallback: boolean) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on', 'enabled'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off', 'disabled'].includes(normalized)) return false;
  }
  return fallback;
}

export function readBranchOrderingSettings(input: unknown): BranchOrderingSettings {
  const record = asOrderingRecord(input);

  const rawEntryCode = record.counter_pickup_entry_code ?? record.counter_takeout_entry_code;
  const entryCode =
    typeof rawEntryCode === 'string' && rawEntryCode.trim().length > 0
      ? rawEntryCode.trim()
      : null;

  return {
    tableOrderingEnabled: readBoolean(
      record.table_ordering_enabled,
      true,
    ),
    counterPickupEnabled: readBoolean(
      record.counter_pickup_ordering_enabled,
      readBoolean(record.counter_takeout_ordering_enabled, false),
    ),
    // The pickup and takeout columns are twins, but several writers (service-layout,
    // pos-setup, with-menu) only ever write the takeout one. Reading pickup-first let the
    // menu page see a stale `false` while the order API, which selects only the takeout
    // column, saw `true` — so the checkout hid the code field and the server rejected the
    // order with "Incorrect access code". Either column being on means the code is required.
    counterPickupRequireEntryCode:
      readBoolean(record.counter_pickup_require_entry_code, false) ||
      readBoolean(record.counter_takeout_require_entry_code, false) ||
      (record.counter_pickup_require_entry_code === undefined &&
        record.counter_takeout_require_entry_code === undefined &&
        Boolean(entryCode)),
    counterPickupRequireGuestName: readBoolean(
      record.counter_pickup_require_guest_name,
      readBoolean(record.counter_takeout_require_guest_name, true),
    ),
    counterPickupEntryCode: entryCode,
    dineInGeofenceRequired: readBoolean(
      record.dine_in_geofence_required,
      true,
    ),
    // Absent column ⇒ `instant`, i.e. unchanged behaviour. A branch has to opt in.
    pickupApprovalMode: normalizePickupApprovalMode(record.pickup_approval_mode),
    pickupPaymentWindowMinutes: normalizePaymentWindowMinutes(
      record.pickup_payment_window_minutes ?? DEFAULT_PAYMENT_WINDOW_MINUTES,
    ),
    // Absent column, or NULL, both mean "not chosen" and BOTH have to stay
    // `null`. Coercing either to `pay_now` here would erase the distinction the
    // migration exists to preserve: the first is an un-migrated environment,
    // the second is an owner who has not opened the setting yet, and for the
    // second one the effective value is inferred from the rail rather than
    // defaulted by this reader.
    pickupPaymentTiming: isPickupPaymentTiming(record.pickup_payment_timing)
      ? record.pickup_payment_timing
      : null,
  };
}

export function buildBranchOrderingSettingsRecord(
  input: BranchOrderingSettings,
) {
  return {
    table_ordering_enabled: Boolean(input.tableOrderingEnabled),
    counter_pickup_ordering_enabled: Boolean(input.counterPickupEnabled),
    counter_takeout_ordering_enabled: Boolean(input.counterPickupEnabled),
    counter_pickup_require_entry_code: Boolean(input.counterPickupRequireEntryCode),
    counter_takeout_require_entry_code: Boolean(input.counterPickupRequireEntryCode),
    counter_pickup_require_guest_name: Boolean(input.counterPickupRequireGuestName),
    counter_takeout_require_guest_name: Boolean(input.counterPickupRequireGuestName),
    counter_pickup_entry_code: input.counterPickupEntryCode?.trim() || null,
    counter_takeout_entry_code: input.counterPickupEntryCode?.trim() || null,
    dine_in_geofence_required: Boolean(input.dineInGeofenceRequired),
  };
}

/**
 * The pickup-approval columns, as a SEPARATE record.
 *
 * Deliberately not folded into `buildBranchOrderingSettingsRecord`: that builder
 * is spread into `branches` updates by the service-layout and pos-setup routes,
 * and PostgREST fails the WHOLE statement on an unknown column. Including two
 * columns that only exist after `20261023000000` would break every branch-settings
 * save on any environment where the migration has not run yet.
 *
 * Callers opt in, which means the opt-in can be added at the same time as the
 * settings UI — by which point the migration is a prerequisite anyone can see.
 */
export function buildPickupApprovalRecord(input: {
  pickupApprovalMode: PickupApprovalMode;
  pickupPaymentWindowMinutes: number;
  /**
   * `null` writes SQL NULL — the owner explicitly leaving this on the inherited
   * default, which is different from never having submitted the form. The
   * distinction is what lets the settings page later show which branches have
   * actually made the decision.
   */
  pickupPaymentTiming?: PickupPaymentTiming | null;
}) {
  return {
    pickup_approval_mode: normalizePickupApprovalMode(input.pickupApprovalMode),
    pickup_payment_window_minutes: normalizePaymentWindowMinutes(input.pickupPaymentWindowMinutes),
    ...(input.pickupPaymentTiming !== undefined
      ? { pickup_payment_timing: isPickupPaymentTiming(input.pickupPaymentTiming) ? input.pickupPaymentTiming : null }
      : {}),
  };
}
